import { usePageTitle } from "@/hooks/use-page-title";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Search, Edit, DollarSign } from "lucide-react";

interface CommunityFund {
  id: string;
  name: string;
  description: string | null;
  category: string;
  balance: number;
  totalRaised: number;
  isActive: boolean;
  isTest: boolean;
  nameEs?: string | null;
  nameSo?: string | null;
  nameHmn?: string | null;
  descriptionEs?: string | null;
  descriptionSo?: string | null;
  descriptionHmn?: string | null;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
}

type EditFundData = {
  name: string;
  description: string;
  nameEs: string;
  descriptionEs: string;
  nameSo: string;
  descriptionSo: string;
  nameHmn: string;
  descriptionHmn: string;
  isActive: boolean;
};

export default function AdminCommunityFundsPage() {
  usePageTitle("Admin — Community Funds");
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingFund, setEditingFund] = useState<CommunityFund | null>(null);
  const [editData, setEditData] = useState<EditFundData>({
    name: "", description: "",
    nameEs: "", descriptionEs: "",
    nameSo: "", descriptionSo: "",
    nameHmn: "", descriptionHmn: "",
    isActive: true,
  });

  const { data: funds, isLoading } = useQuery<CommunityFund[]>({
    queryKey: ["/api/admin/community-funds"],
  });

  const editMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<EditFundData> }) => {
      return apiRequest("PATCH", `/api/admin/community-funds/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/community-funds"] });
      queryClient.invalidateQueries({ queryKey: ["/api/community-funds"] });
      toast({ title: "Community fund updated successfully" });
      setEditDialogOpen(false);
      setEditingFund(null);
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update fund",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleOpenEdit = (fund: CommunityFund) => {
    setEditingFund(fund);
    setEditData({
      name: fund.name ?? "",
      description: fund.description ?? "",
      nameEs: fund.nameEs ?? "",
      descriptionEs: fund.descriptionEs ?? "",
      nameSo: fund.nameSo ?? "",
      descriptionSo: fund.descriptionSo ?? "",
      nameHmn: fund.nameHmn ?? "",
      descriptionHmn: fund.descriptionHmn ?? "",
      isActive: fund.isActive ?? true,
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (!editingFund) return;
    editMutation.mutate({ id: editingFund.id, data: editData });
  };

  const filtered = funds?.filter(f =>
    f.name.toLowerCase().includes(search.toLowerCase()) ||
    f.category.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold">Community Funds</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage community health fund details and translations
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <CardTitle>All Funds</CardTitle>
                <CardDescription>{funds?.length ?? 0} community funds</CardDescription>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search funds..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 w-64"
                  data-testid="input-search-funds"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fund</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Balance</TableHead>
                    <TableHead>Total Raised</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!filtered?.length ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No community funds found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((fund) => (
                      <TableRow key={fund.id} data-testid={`row-fund-${fund.id}`}>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <span className="font-medium">{fund.name}</span>
                            <div className="flex items-center gap-1 flex-wrap">
                              <Badge variant={fund.nameEs ? "default" : "outline"} className="text-xs">ES</Badge>
                              <Badge variant={fund.nameSo ? "default" : "outline"} className="text-xs">SO</Badge>
                              <Badge variant={fund.nameHmn ? "default" : "outline"} className="text-xs">HMN</Badge>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{fund.category}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <DollarSign className="h-3 w-3 text-muted-foreground" />
                            <span>{formatCurrency(fund.balance)}</span>
                          </div>
                        </TableCell>
                        <TableCell>{formatCurrency(fund.totalRaised)}</TableCell>
                        <TableCell>
                          <Badge variant={fund.isActive ? "default" : "secondary"}>
                            {fund.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`button-edit-fund-${fund.id}`}
                            onClick={() => handleOpenEdit(fund)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={editDialogOpen}
        onOpenChange={(open) => { if (!open) { setEditDialogOpen(false); setEditingFund(null); } }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Community Fund</DialogTitle>
            <DialogDescription>
              Update fund details and translations. Filled language badges appear in the list.
            </DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="en" className="mt-2">
            <TabsList className="flex gap-1">
              <TabsTrigger value="en" data-testid="tab-fund-lang-en">English</TabsTrigger>
              <TabsTrigger value="es" data-testid="tab-fund-lang-es">
                Español
                {editData.nameEs ? <span className="ml-1 h-1.5 w-1.5 rounded-full bg-green-500 inline-block" /> : null}
              </TabsTrigger>
              <TabsTrigger value="so" data-testid="tab-fund-lang-so">
                Somali
                {editData.nameSo ? <span className="ml-1 h-1.5 w-1.5 rounded-full bg-green-500 inline-block" /> : null}
              </TabsTrigger>
              <TabsTrigger value="hmn" data-testid="tab-fund-lang-hmn">
                Hmong
                {editData.nameHmn ? <span className="ml-1 h-1.5 w-1.5 rounded-full bg-green-500 inline-block" /> : null}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="en" className="mt-4 space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-fund-name-en">Name (English)</Label>
                <Input
                  id="edit-fund-name-en"
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  data-testid="input-edit-fund-name-en"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-fund-desc-en">Description (English)</Label>
                <Textarea
                  id="edit-fund-desc-en"
                  value={editData.description}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                  rows={4}
                  data-testid="input-edit-fund-desc-en"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editData.isActive}
                  onChange={(e) => setEditData({ ...editData, isActive: e.target.checked })}
                  data-testid="checkbox-edit-fund-active"
                />
                <span className="text-sm">Active</span>
              </label>
            </TabsContent>

            <TabsContent value="es" className="mt-4 space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-fund-name-es">Name (Español)</Label>
                <Input
                  id="edit-fund-name-es"
                  value={editData.nameEs}
                  onChange={(e) => setEditData({ ...editData, nameEs: e.target.value })}
                  placeholder="Nombre en español"
                  data-testid="input-edit-fund-name-es"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-fund-desc-es">Description (Español)</Label>
                <Textarea
                  id="edit-fund-desc-es"
                  value={editData.descriptionEs}
                  onChange={(e) => setEditData({ ...editData, descriptionEs: e.target.value })}
                  placeholder="Descripción en español"
                  rows={4}
                  data-testid="input-edit-fund-desc-es"
                />
              </div>
            </TabsContent>

            <TabsContent value="so" className="mt-4 space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-fund-name-so">Name (Somali)</Label>
                <Input
                  id="edit-fund-name-so"
                  value={editData.nameSo}
                  onChange={(e) => setEditData({ ...editData, nameSo: e.target.value })}
                  placeholder="Magaca Soomaaliga"
                  data-testid="input-edit-fund-name-so"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-fund-desc-so">Description (Somali)</Label>
                <Textarea
                  id="edit-fund-desc-so"
                  value={editData.descriptionSo}
                  onChange={(e) => setEditData({ ...editData, descriptionSo: e.target.value })}
                  placeholder="Sharraxaadda Soomaaliga"
                  rows={4}
                  data-testid="input-edit-fund-desc-so"
                />
              </div>
            </TabsContent>

            <TabsContent value="hmn" className="mt-4 space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-fund-name-hmn">Name (Hmong)</Label>
                <Input
                  id="edit-fund-name-hmn"
                  value={editData.nameHmn}
                  onChange={(e) => setEditData({ ...editData, nameHmn: e.target.value })}
                  placeholder="Lub npe Hmoob"
                  data-testid="input-edit-fund-name-hmn"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-fund-desc-hmn">Description (Hmong)</Label>
                <Textarea
                  id="edit-fund-desc-hmn"
                  value={editData.descriptionHmn}
                  onChange={(e) => setEditData({ ...editData, descriptionHmn: e.target.value })}
                  placeholder="Piav qhia Hmoob"
                  rows={4}
                  data-testid="input-edit-fund-desc-hmn"
                />
              </div>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditDialogOpen(false); setEditingFund(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={editMutation.isPending}
              data-testid="button-save-fund"
            >
              {editMutation.isPending ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
